import { CONTACT_SALES_URL } from '../../constants';
import { useDispatch, useSelector } from '../../../../../store/store';
import { PricingPlan } from '../../../../../store/workspace/types';
import CheckBox from '../../../../../components/CheckBox';
import Modal from '../../../../../components/Modal';
import styles from './style.module.scss';
import { accountPlans } from '../../../../../configs';
import { PlansTab } from '../../types';
import { ReactNode, useState } from 'react';
import {
    selectProfile,
    selectSelectedPricingPlan,
} from '../../../../../store/workspace/selectors';
import {
    setPricingPlan,
    setPlansModalShow,
    setPurchasePlanModal,
} from '../../../../../store/workspace/slice';
import {
    LayerIcon,
    GemSquareIcon,
    FlagSquareIcon,
    ArrowRightIcon,
    TargetSquareIcon,
} from '../../../../../assets/icons';

const modalDescription = 'Please choose a plan or contact sales to continue.';

/** Icon each plan of the pricing config asks for, by its `icon` key. */
const planIcons: Record<string, ReactNode> = {
    gem: <GemSquareIcon />,
    flag: <FlagSquareIcon />,
    target: <TargetSquareIcon />,
};

/** Price list the account owner picks the next plan from. */
export default function PlansModal() {
    const dispatch = useDispatch();
    const profile = useSelector(selectProfile);
    const [tab, setTab] = useState<PlansTab>('yearly');
    const selectedPlan = useSelector(selectSelectedPricingPlan);

    const closeModal = () => dispatch(setPlansModalShow(false));

    const confirmFunc = () => {
        closeModal();
        dispatch(setPurchasePlanModal(true));
    };

    /** A plan is only selectable when it is a step up from the running one. */
    const setPlan = (plan: PricingPlan) => {
        if (!plan.active) return;
        if (profile?.planType === plan.id) return;
        if (profile?.planType?.includes('professional') && plan.name === 'Standard') return;
        if (profile?.planType === 'standard_annual' && plan.id === 'standard_monthly') return;
        dispatch(setPricingPlan(plan));
    };

    const changeTab = (newTab: PlansTab) => {
        if (newTab === tab) return;
        setTab(newTab);
        dispatch(setPricingPlan(null));
    };

    return (
        <Modal
            size='xxl'
            actions='both'
            title='Select plan'
            restrictActionsWidth
            icon={<LayerIcon />}
            closeFunc={closeModal}
            description={modalDescription}
            childClassName={styles.wrapper}
            dismissButton={{
                text: 'Cancel',
                type: 'Outline-Primary',
                onClick: closeModal,
            }}
            confirmButton={{
                text: 'Confirm',
                type: 'Primary',
                disabled: !selectedPlan,
                onClick: confirmFunc,
            }}
        >
            <div className={styles.banner}>
                <span className={styles.banner_text}>
                    Upgrade to enterprise for more features by contacting sales.
                </span>
                <div
                    className={styles.banner_btn}
                    onClick={() => window.open(CONTACT_SALES_URL, '_blank')}
                >
                    <span>Contact sales</span>
                    <ArrowRightIcon />
                </div>
            </div>

            <div className={styles.tabs}>
                <div
                    className={styles.tab}
                    data-is-active={tab === 'yearly'}
                    onClick={() => changeTab('yearly')}
                >
                    Yearly -20% off
                </div>
                <div
                    className={styles.tab}
                    data-is-active={tab === 'monthly'}
                    onClick={() => changeTab('monthly')}
                >
                    Monthly
                </div>
            </div>

            <div className={styles.plan_list}>
                {accountPlans[tab].map(plan => {
                    const isCurrentPlan = profile?.planType === plan.id;
                    const isBasic = plan.name === 'Basic';
                    const coverText =
                        isCurrentPlan ? 'Current plan' :
                            isBasic ? 'You have already used this plan' : null;

                    return (
                        <div
                            key={plan.id}
                            className={styles.plan}
                            onClick={() => setPlan(plan)}
                            data-is-selected={selectedPlan?.id === plan.id}
                        >
                            {!isCurrentPlan && !isBasic && <CheckBox
                                checked={selectedPlan?.id === plan.id}
                                className={styles.plan_checkBox}
                                onClick={() => setPlan(plan)}
                            />}

                            {planIcons[plan.icon]}

                            <div className={styles.plan_name}>
                                {plan.name}
                                {plan.name === 'Standard' &&
                                    <span className={styles.plan_name_badge}>Popular</span>}
                            </div>

                            <span className={styles.plan_slogan}>{plan.slogan}</span>
                            <span className={styles.plan_price}>{plan.price} <b>{plan.per}</b> </span>

                            <div className={styles.plan_check_list}>
                                {plan.checkList.map(item => <div
                                    key={item}
                                    className={styles.plan_check_list_item}
                                >
                                    <span className={styles.text}>{item}</span>
                                </div>)}
                            </div>

                            {coverText && <div className={styles.plan_cover}>
                                <span>{coverText}</span>
                            </div>}
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};
